<template lang="pug">
v-dialog(v-model="dialog" width="760" persistent)
  v-card
    v-card-title Estacionalidad de la demanda
    v-card-subtitle
      | Cuándo compran tus clientes durante el año. Es distinto de cuándo puedes
      | producir: eso se declara como capacidad en cada producto o servicio.
    v-card-text
      v-alert.mb-4(dense text type="info")
        | El Mix Óptimo reparte la venta siguiendo este perfil. Si tu negocio es
        | parejo todo el año, déjalo plano. No necesitas que sume 100 exacto:
        | se usa la proporción entre los meses.

      .mb-4
        .text-caption.font-weight-bold.mb-2 Partir de un perfil
        v-btn.mr-2.mb-2(small outlined @click="aplicarPerfil('plano')") Plano
        v-btn.mr-2.mb-2(small outlined @click="aplicarPerfil('segundoSemestre')") 70% en el 2º semestre
        v-btn.mr-2.mb-2(small outlined @click="aplicarPerfil('primerSemestre')") 70% en el 1er semestre
        v-btn.mb-2(small outlined @click="aplicarPerfil('pesca')") Temporada pesquera

      v-row(dense)
        v-col(v-for="mes in meses" :key="mes.field" cols="6" sm="4" md="3")
          v-text-field(
            v-model.number="form[mes.field]"
            :label="mes.label"
            outlined
            dense
            type="number"
            min="0"
            suffix="%"
            hide-details
          )

      .d-flex.align-center.mt-4
        v-chip(small :color="sumaColor" text-color="white") Suma: {{ suma.toFixed(2) }}%
        span.text-caption.grey--text.ml-3(v-if="!hayDatos") Con todos los meses en 0 se usa reparto plano.
        span.text-caption.grey--text.ml-3(v-else) Reparto efectivo: {{ repartoTexto }}

    v-card-actions
      v-spacer
      v-btn(text @click="close" :disabled="loading") Cancelar
      v-btn(color="primary" depressed @click="onSubmit" :loading="loading") Guardar y recalcular
</template>

<script>
import { mapActions } from 'vuex'

const MESES = [
  { field: 'january_demand_percentage', label: 'Enero' },
  { field: 'february_demand_percentage', label: 'Febrero' },
  { field: 'march_demand_percentage', label: 'Marzo' },
  { field: 'april_demand_percentage', label: 'Abril' },
  { field: 'may_demand_percentage', label: 'Mayo' },
  { field: 'june_demand_percentage', label: 'Junio' },
  { field: 'july_demand_percentage', label: 'Julio' },
  { field: 'august_demand_percentage', label: 'Agosto' },
  { field: 'september_demand_percentage', label: 'Septiembre' },
  { field: 'october_demand_percentage', label: 'Octubre' },
  { field: 'november_demand_percentage', label: 'Noviembre' },
  { field: 'december_demand_percentage', label: 'Diciembre' },
]

// Perfiles de partida. No pretenden ser exactos: son un punto de arranque que el
// usuario ajusta mes a mes con su propia realidad.
const PERFILES = {
  plano: [8.33, 8.33, 8.33, 8.33, 8.33, 8.33, 8.33, 8.33, 8.33, 8.33, 8.33, 8.33],
  segundoSemestre: [5, 5, 5, 5, 5, 5, 11.67, 11.67, 11.67, 11.67, 11.66, 11.66],
  primerSemestre: [11.67, 11.67, 11.67, 11.67, 11.66, 11.66, 5, 5, 5, 5, 5, 5],
  pesca: [2, 2, 3, 5, 8, 10, 14, 16, 16, 12, 7, 5],
}

export default {
  name: 'DemandSeasonalityFormModal',
  data() {
    return {
      dialog: false,
      loading: false,
      projectionId: null,
      meses: MESES,
      form: {},
    }
  },
  computed: {
    suma() {
      return MESES.reduce((acc, m) => acc + (Number(this.form[m.field]) || 0), 0)
    },
    hayDatos() {
      return this.suma > 0
    },
    sumaColor() {
      if (!this.hayDatos) return 'grey'
      // No es un error que no sume 100: el backend normaliza por la suma real.
      return Math.abs(this.suma - 100) <= 1 ? 'success' : 'warning'
    },
    // Muestra el reparto real (normalizado) del semestre, que es lo que el
    // usuario quiere verificar: "¿quedó mi 70/30?"
    repartoTexto() {
      const v = MESES.map(m => Number(this.form[m.field]) || 0)
      const total = v.reduce((a, b) => a + b, 0)
      if (!total) return ''
      const h1 = v.slice(0, 6).reduce((a, b) => a + b, 0) / total * 100
      return `1er semestre ${h1.toFixed(0)}% · 2º semestre ${(100 - h1).toFixed(0)}%`
    },
  },
  methods: {
    ...mapActions('commercial_projection', [
      'getCommercialProjection', 'editCommercialProjectionSeasonality',
    ]),
    aplicarPerfil(nombre) {
      const valores = PERFILES[nombre]
      MESES.forEach((m, i) => { this.form[m.field] = valores[i] })
      this.form = { ...this.form }
    },
    async open(projectionId) {
      this.projectionId = projectionId
      this.aplicarPerfil('plano')
      this.dialog = true
      this.loading = true
      const res = await this.getCommercialProjection(projectionId)
      this.loading = false
      const d = res && res.data
      if (!d) return
      const cargado = {}
      MESES.forEach(m => {
        cargado[m.field] = d[m.field] !== undefined && d[m.field] !== null
          ? Number(d[m.field])
          : this.form[m.field]
      })
      this.form = cargado
    },
    close() {
      this.dialog = false
    },
    async onSubmit() {
      if (!this.hayDatos) {
        this.$toast.error('Pon al menos un mes con demanda mayor a 0.')
        return
      }
      this.loading = true
      const data = {}
      MESES.forEach(m => { data[m.field] = Number(this.form[m.field]) || 0 })
      const res = await this.editCommercialProjectionSeasonality({ id: this.projectionId, data })
      this.loading = false
      if (res && !res.error) {
        this.$toast.success('Estacionalidad guardada. El Mix Óptimo se recalculó.')
        this.$emit('save')
        this.close()
      } else {
        const msg = (res && res.data && res.data.detail) || 'No se pudo guardar.'
        this.$toast.error(msg)
      }
    },
  },
}
</script>
